import { useQuery } from '@tanstack/react-query';
import { ProtectedImage } from '@/components/media';
import { Megaphone } from 'lucide-react';
import { Badge, EmptyState, LoadingBlock, Note } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { publicNewsQuery } from '@/lib/public-news';
import { newsKindTone } from '@/lib/labels';
import type { NewsRow } from '@/lib/types';
import { PublicLayout } from '@/layouts/PublicLayout';
import { useT } from '@/i18n/context';
import type { MessageId } from '@/i18n/messages';

/**
 * بطاقة خبرٍ عام — تُستعمل في /news وفي قسم «آخر الأخبار» على الصفحة الأولى.
 * `compact` تقصّ النصّ إلى بضعة أسطر (الصفحة الأولى)؛ وبدونها يظهر كاملاً.
 */
export function PublicNewsCard({ row, compact = false }: { row: NewsRow; compact?: boolean }) {
  const t = useT();
  return (
    <article className={`news-card${compact ? ' news-card--compact' : ''}`}>
      <header className="news-card__head">
        <Megaphone size={16} aria-hidden />
        <h3 className="news-card__title">{row.titleAr}</h3>
        <Badge tone={newsKindTone[row.kind]}>{t(`news.kind.${row.kind}` as MessageId)}</Badge>
      </header>
      {row.imageFileId && <ProtectedImage path={`/public/news/${row.id}/image`} alt="" size={200} className="news-card__img" anonymous />}
      {row.subjectUser && <strong className="small">{row.subjectUser.fullName}</strong>}
      <p className="news-card__body">{row.bodyAr}</p>
      <footer className="news-card__foot caption">
        <span>{t('news.public.by')}</span>
        {row.publishedAt && <span className="num">{fmtDate(row.publishedAt)}</span>}
      </footer>
    </article>
  );
}

/** /news — أخبار المنصة المنشورة، بلا دخول (GET /public/news) */
export function NewsPublicPage() {
  const t = useT();
  const q = useQuery(publicNewsQuery());
  const rows = q.data ?? [];
  return (
    <PublicLayout width={860}>
      <div className="stack gap-2">
        <h1 style={{ margin: 0 }}>{t('news.public.title')}</h1>
      </div>
      {q.isLoading && <LoadingBlock label={t('common.loadingScreen')} />}
      {q.isError && <Note kind="danger">{(q.error as ApiError).message}</Note>}
      {q.isSuccess && (rows.length === 0 ? (
        <EmptyState art="news" title={t('news.public.empty.title')} text="" />
      ) : (
        <div className="stack gap-4">
          {rows.map((n) => <PublicNewsCard key={n.id} row={n} />)}
        </div>
      ))}
    </PublicLayout>
  );
}
